import { ContentfulImage, DataGridPro, Section, useReasonMessages } from '@cibo/ui'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Field, TaskContentDetailsLocked } from '@cibo/core'
import { fieldNameColumn } from '@cibo/landmanager'
import { useMemo } from 'react'
import { fieldTaskEligibilityColumn } from '../../../components'
import { useProgramEngagementFields, useProgramModel } from '../../../queries'
import { TaskMarkdown } from '../TaskMarkdown'
import { DetailsLockedProps } from './types'

export const DetailsLockedFailed = ({
  abandonPath,
  engagement,
  ownerName,
  task,
  userRole,
}: Omit<DetailsLockedProps, 'navigateToNextTask'>) => {
  const { t } = useTranslation('@cibo/programs/DetailsLockedFailed')
  const program = useProgramModel({ programId: engagement.programId })
  const taskState = engagement.getTaskStatus(task.id)
  const reasonMessages = useReasonMessages()
  const taskContent = program.data?.taskContent<TaskContentDetailsLocked>(task.id)
  const nameColumn = fieldNameColumn({ t })
  const fields = useProgramEngagementFields({ id: engagement.id })
  const reasonsColumn = fieldTaskEligibilityColumn({
    t,
    eligibility: taskState?.status?.eligibility,
    ...reasonMessages,
  })

  const ineligibleFields = fields.data?.items?.filter(({ resourceId }) =>
    taskState?.status.eligibility?.reasonItems?.some(item => item.id === resourceId)
  )
  const ineligibleUserReasons = engagement.ineligibleTaskUserReasons(task.id)
  const ineligibleEngagementReasons = engagement.ineligibleTaskEngagementReasons(task.id)
  const { ineligibleColumns } = useMemo(
    () => ({
      ineligibleColumns: [nameColumn, reasonsColumn],
    }),
    []
  )

  return (
    <Stack spacing={3} flex={1}>
      <Section sx={{ flex: 1 }}>
        <Stack padding={3} spacing={3}>
          <Stack direction="row" spacing={3} alignItems={'center'} justifyContent={'space-between'}>
            {program.data?.promoImages?.[0] && (
              <ContentfulImage
                asset={program.data.promoImages[0]}
                style={{ borderRadius: '50%', height: 75, width: 75 }}
              />
            )}
            <Box flex={1}>
              <TaskMarkdown>
                {taskContent?.content.detailsLocked.invalid.markdown?.[userRole]?.replace(
                  '{{name}}',
                  ownerName
                )}
              </TaskMarkdown>
            </Box>
            {!!abandonPath && (
              <Button
                component={Link}
                to={abandonPath}
                variant="contained"
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('exploreOtherPrograms')}
              </Button>
            )}
          </Stack>
          {/* yep nested section is intentional here */}
          {!!ineligibleUserReasons.length && (
            <Section>
              <Stack padding={3} spacing={3}>
                <Typography color="text.secondary">
                  {t(`whyIsUserNotEligible_${userRole}`, { name: ownerName })}
                </Typography>
                <Stack>
                  {ineligibleUserReasons.map(reasonItem =>
                    reasonItem.reasons?.map(({ reason, traitId }) => (
                      <Typography key={reason}>
                        {reasonMessages.byReasonCode({ reasonCode: reason, traitId })}
                      </Typography>
                    ))
                  )}
                </Stack>
              </Stack>
            </Section>
          )}
          {!!ineligibleEngagementReasons.length && (
            <Section>
              <Stack padding={3} spacing={3}>
                <Typography color="text.secondary">
                  {t(`whyIsUserNotEligible_${userRole}`, { name: ownerName })}
                </Typography>
                <Stack>
                  {ineligibleEngagementReasons.map(reasonItem =>
                    reasonItem.reasons?.map(({ reason, traitId }) => (
                      <Typography key={reason}>
                        {reasonMessages.byReasonCode({ reasonCode: reason, traitId })}
                      </Typography>
                    ))
                  )}
                </Stack>
              </Stack>
            </Section>
          )}
        </Stack>
      </Section>
      {!!ineligibleFields?.length && (
        <Section>
          <Stack padding={3} spacing={3}>
            <Typography>{t('ineligibleField', { count: ineligibleFields.length })}</Typography>
            <DataGridPro
              columns={ineligibleColumns}
              rows={ineligibleFields}
              getRowHeight={() => 'auto'}
              getRowId={(row: Field) => row.resourceId}
              autoHeight
              disableSelectionOnClick
              hideFooter
              hideCellFocus
            />
          </Stack>
        </Section>
      )}
    </Stack>
  )
}
